/**
 * CheckoutDialog Component
 * Main checkout/settlement dialog for POS
 * Handles Cash, M-Pesa, Card, NFC, Credit Account, and Pesapal payments
 * Supports partial payments via editable "Amount Tendered" field
 */

import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Banknote, Phone, CreditCard, Smartphone, X, Loader2, 
  CheckCircle, RefreshCw, Search, Check, UserCheck, AlertTriangle, User, Star
} from "lucide-react";
import { cn } from "../../lib/utils";

export function CheckoutDialog({
  open,
  onOpenChange,
  activeInvoice,
  finalTotal,
  formatCurrency,
  // Discount
  canApplyDiscount,
  appliedDiscount,
  promoDiscount,
  discountCode,
  setDiscountCode,
  setAppliedDiscount,
  setPromoDiscount,
  applyDiscountCode,
  // Payment method
  paymentMethod,
  setPaymentMethod,
  noCashPayments,
  cardSettings,
  dpoSettings,
  pesapalPosSettings,
  // Card details
  cardDetails,
  setCardDetails,
  // NFC
  nfcPaymentStatus,
  setNfcPaymentStatus,
  nfcPollingInterval,
  setNfcPaymentRequestId,
  // M-Pesa
  mpesaSettings,
  mpesaPhone,
  setMpesaPhone,
  mpesaMethod,
  setMpesaMethod,
  // C2B
  loadingC2BPayments,
  availableC2BPayments,
  fetchAvailableC2BPayments,
  selectedC2BPayment,
  setSelectedC2BPayment,
  c2bMpesaCode,
  setC2bMpesaCode,
  c2bVerifyResult,
  setC2bVerifyResult,
  c2bVerifying,
  handleVerifyC2BPayment,
  // Credit Sale
  selectedCustomer,
  setSelectedCustomer,
  creditCheckResult,
  checkCreditAvailability,
  creditApprovalPending,
  // Credit sale permission + customer search
  canCreditSale,
  customerSearch,
  setCustomerSearch,
  fetchCustomers,
  customersLoading,
  customersList,
  // Actions
  processing,
  handleCloseInvoice,
  handleExpressCheckout,
  // Amount tendered
  amountTendered,
  setAmountTendered,
}) {
  // Outstanding balance (accounts for previous partial payments)
  const amountPaid = activeInvoice?.amount_paid || 0;
  const outstanding = Math.max(0, finalTotal - amountPaid);
  const isPartiallyPaid = amountPaid > 0;

  // Pre-populate amount tendered with outstanding balance when dialog opens
  useEffect(() => {
    if (open && setAmountTendered) {
      setAmountTendered(outstanding.toFixed(2));
    }
  }, [open, outstanding, setAmountTendered]);

  const tenderedNum = parseFloat(amountTendered) || 0;
  const isPartialPayment = tenderedNum > 0 && tenderedNum < outstanding - 0.01;
  const changeAmount = Math.max(0, tenderedNum - outstanding);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle>{activeInvoice ? `Settle Invoice` : "Checkout"}</DialogTitle>
          <DialogDescription>
            {isPartiallyPaid ? (
              <span>
                Total: {formatCurrency(finalTotal)} | Paid: <span className="text-green-600 font-semibold">{formatCurrency(amountPaid)}</span> | Outstanding: <span className="font-mono font-bold text-primary">{formatCurrency(outstanding)}</span>
              </span>
            ) : (
              <span>Total: <span className="font-mono font-bold text-primary">{formatCurrency(finalTotal)}</span></span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Partially paid badge */}
          {isPartiallyPaid && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800" data-testid="partial-paid-badge">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-700 dark:text-amber-300">
                Partially paid — {formatCurrency(amountPaid)} received previously
              </span>
            </div>
          )}

          {/* Discount Section */}
          {canApplyDiscount && !isPartiallyPaid && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Discount Code</p>
              {appliedDiscount ? (
                <div className="bg-green-500/10 p-2 rounded-lg flex justify-between items-center">
                  <span className="text-sm text-green-600 font-medium">{appliedDiscount.code}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-green-600">-{formatCurrency(promoDiscount)}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => { setAppliedDiscount(null); setPromoDiscount(0); setDiscountCode(""); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input 
                    placeholder="Enter discount code" 
                    value={discountCode} 
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} 
                    className="h-10"
                    data-testid="discount-code-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={applyDiscountCode} 
                    className="h-10"
                    data-testid="apply-discount-btn"
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Payment Method</p>
            {(() => {
              const cardRails = [
                { id: "card_manual", label: "Manual Card", testid: "payment-card-manual-btn" },
                pesapalPosSettings?.enabled && { id: "pesapal_pos", label: "Pesapal Card", testid: "payment-pesapal-pos-btn" },
                dpoSettings?.enabled && { id: "dpo_card", label: "Network Card", testid: "payment-dpo-btn" },
                cardSettings?.enabled && { id: "card", label: "Card (Cybersource)", testid: "payment-card-cybersource-btn" },
                (cardSettings?.enabled && cardSettings?.nfc_enabled) && { id: "nfc", label: "Tap / NFC", testid: "payment-nfc-btn" },
              ].filter(Boolean);
              const mpesaRails = [
                { id: "mpesa", label: "M-Pesa Direct", testid: "payment-mpesa-direct-btn" },
                pesapalPosSettings?.enabled && { id: "pesapal_mpesa", label: "Pesapal M-Pesa", testid: "payment-pesapal-mpesa-btn" },
                dpoSettings?.enabled && { id: "dpo_mpesa", label: "Network M-Pesa", testid: "payment-dpo-mpesa-btn" },
              ].filter(Boolean);
              const cardIds = cardRails.map((r) => r.id);
              const mpesaIds = mpesaRails.map((r) => r.id);
              const showCredit = selectedCustomer?.is_debtor || canCreditSale;
              const activeGroup =
                paymentMethod === "cash" ? "cash" :
                paymentMethod === "credit" ? "credit" :
                mpesaIds.includes(paymentMethod) ? "mpesa" : "card";

              const selectMpesa = () => setPaymentMethod(mpesaRails[0].id);
              const selectCard = () => setPaymentMethod(cardRails[0].id);
              const selectCredit = () => {
                setPaymentMethod("credit");
                if (selectedCustomer?.is_debtor && checkCreditAvailability) checkCreditAvailability(finalTotal);
              };

              const Tile = ({ active, onClick, icon: Icon, label, testid, iconClass }) => (
                <Button variant={active ? "default" : "outline"} className="h-16 flex-col gap-1" onClick={onClick} data-testid={testid}>
                  <Icon className={cn("h-6 w-6", iconClass)} /><span className="text-xs">{label}</span>
                </Button>
              );

              return (
                <div className="space-y-3">
                  {/* Primary payment type */}
                  <div className={cn("grid gap-3", showCredit ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3")}>
                    {!noCashPayments && (
                      <Tile active={activeGroup === "cash"} onClick={() => setPaymentMethod("cash")} icon={Banknote} label="Cash" testid="payment-cash-btn" />
                    )}
                    <Tile active={activeGroup === "mpesa"} onClick={selectMpesa} icon={Phone} label="M-Pesa" testid="payment-mpesa-btn" iconClass="text-green-600" />
                    <Tile active={activeGroup === "card"} onClick={selectCard} icon={CreditCard} label="Card" testid="payment-card-btn" iconClass="text-blue-600" />
                    {showCredit && (
                      <Tile active={activeGroup === "credit"} onClick={selectCredit} icon={UserCheck} label="Credit" testid="payment-credit-btn" />
                    )}
                  </div>

                  {/* M-Pesa rails (only when >1 option) */}
                  {activeGroup === "mpesa" && mpesaRails.length > 1 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-1 border-l-2 border-green-500/40" data-testid="mpesa-rails">
                      {mpesaRails.map((r) => (
                        <Button key={r.id} size="sm" variant={paymentMethod === r.id ? "default" : "outline"}
                          className="h-10 text-xs" onClick={() => setPaymentMethod(r.id)} data-testid={r.testid}>
                          {r.label}
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* Card rails (only when >1 option) */}
                  {activeGroup === "card" && cardRails.length > 1 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-1 border-l-2 border-blue-500/40" data-testid="card-rails">
                      {cardRails.map((r) => (
                        <Button key={r.id} size="sm" variant={paymentMethod === r.id ? "default" : "outline"}
                          className="h-10 text-xs" onClick={() => setPaymentMethod(r.id)} data-testid={r.testid}>
                          {r.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {noCashPayments && (
              <p className="text-xs text-muted-foreground text-center">Cash payments restricted for this user</p>
            )}
          </div>

          {/* === AMOUNT TENDERED === */}
          <div className="space-y-2 p-3 rounded-lg border bg-muted/30" data-testid="amount-tendered-section">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Amount Tendered</Label>
              {outstanding > 0 && tenderedNum !== outstanding && (
                <Button 
                  variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => setAmountTendered(outstanding.toFixed(2))}
                  data-testid="reset-tendered-btn"
                >
                  Reset to {formatCurrency(outstanding)}
                </Button>
              )}
            </div>
            <Input
              type="number"
              value={amountTendered}
              onChange={(e) => setAmountTendered(e.target.value)}
              className="h-12 text-xl font-mono font-bold text-center"
              min="0"
              max={outstanding}
              step="0.01"
              data-testid="amount-tendered-input"
            />
            {isPartialPayment && (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400" data-testid="partial-payment-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Partial payment — invoice will remain open with <strong>{formatCurrency(outstanding - tenderedNum)}</strong> outstanding</span>
              </div>
            )}
            {changeAmount > 0.01 && paymentMethod === "cash" && (
              <div className="flex items-center justify-between text-sm font-medium text-green-600">
                <span>Change Due</span>
                <span className="font-mono" data-testid="change-due">{formatCurrency(changeAmount)}</span>
              </div>
            )}
          </div>

          {/* Credit Payment Section */}
          {paymentMethod === "credit" && (
            <CreditPaymentSection
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
              creditCheckResult={creditCheckResult}
              checkCreditAvailability={checkCreditAvailability}
              finalTotal={finalTotal}
              outstanding={outstanding}
              formatCurrency={formatCurrency}
              creditApprovalPending={creditApprovalPending}
              canCreditSale={canCreditSale}
              customerSearch={customerSearch}
              setCustomerSearch={setCustomerSearch}
              fetchCustomers={fetchCustomers}
              customersLoading={customersLoading}
              customersList={customersList}
            />
          )}

          {/* Card Payment Form (Cybersource real-time) */}
          {paymentMethod === "card" && (
            <CardPaymentForm cardDetails={cardDetails} setCardDetails={setCardDetails} />
          )}
          {/* Manual Card entry (reference + last 4 digits) */}
          {paymentMethod === "card_manual" && (
            <ManualCardForm cardDetails={cardDetails} setCardDetails={setCardDetails} />
          )}

          {/* DPO Card Terminal (Push to Pay) */}
          {paymentMethod === "dpo_card" && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-start gap-3" data-testid="dpo-info">
              <Smartphone className="h-5 w-5 text-primary mt-0.5" />
              <div className="text-sm text-muted-foreground">
                The bill total will be pushed to the card terminal
                {dpoSettings?.tid ? ` (TID ${dpoSettings.tid})` : ""}. Ask the customer to tap or insert their card on the device.
              </div>
            </div>
          )}

          {/* Pesapal M-Pesa (STK push via Pesapal) */}
          {paymentMethod === "pesapal_mpesa" && (
            <div className="rounded-lg border border-green-300/40 bg-green-500/5 p-4 space-y-3" data-testid="pesapal-mpesa-info">
              <div className="flex items-start gap-3">
                <Smartphone className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  An M-Pesa STK prompt will be sent to the customer phone via Pesapal. The amount is rounded to the nearest shilling.
                </div>
              </div>
              <div>
                <Label htmlFor="pesapal-mpesa-phone" className="text-xs">Customer M-Pesa Number</Label>
                <Input
                  id="pesapal-mpesa-phone"
                  value={mpesaPhone}
                  onChange={(e) => setMpesaPhone(e.target.value)}
                  placeholder="0712345678"
                  data-testid="pesapal-mpesa-phone-input"
                />
              </div>
            </div>
          )}

          {/* Network (DPO) M-Pesa (STK push via terminal MoMo module) */}
          {paymentMethod === "dpo_mpesa" && (
            <div className="rounded-lg border border-green-300/40 bg-green-500/5 p-4 space-y-3" data-testid="dpo-mpesa-info">
              <div className="flex items-start gap-3">
                <Smartphone className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  An M-Pesa prompt will be pushed to the customer phone via the Network terminal
                  {dpoSettings?.terminal_id ? ` (TID ${dpoSettings.terminal_id})` : ""}. The amount is rounded to the nearest shilling.
                </div>
              </div>
              <div>
                <Label htmlFor="dpo-mpesa-phone" className="text-xs">Customer M-Pesa Number</Label>
                <Input
                  id="dpo-mpesa-phone"
                  value={mpesaPhone}
                  onChange={(e) => setMpesaPhone(e.target.value)}
                  placeholder="0712345678"
                  data-testid="dpo-mpesa-phone-input"
                />
              </div>
            </div>
          )}

          {/* Pesapal Card Terminal (Push to Pay) */}
          {paymentMethod === "pesapal_pos" && (
            <div className="rounded-lg border border-blue-300/40 bg-blue-500/5 p-4 flex items-start gap-3" data-testid="pesapal-pos-info">
              <Smartphone className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                The bill total will be pushed to the Pesapal card terminal
                {pesapalPosSettings?.terminal_sn ? ` (SN ${pesapalPosSettings.terminal_sn})` : ""}. Ask the customer to tap or insert their card on the device.
              </div>
            </div>
          )}

          {/* NFC Payment Section */}
          {paymentMethod === "nfc" && cardSettings?.enabled && cardSettings?.nfc_enabled && (
            <NFCPaymentSection
              nfcPaymentStatus={nfcPaymentStatus}
              setNfcPaymentStatus={setNfcPaymentStatus}
              nfcPollingInterval={nfcPollingInterval}
              setNfcPaymentRequestId={setNfcPaymentRequestId}
            />
          )}

          {/* M-Pesa Section */}
          {paymentMethod === "mpesa" && mpesaSettings?.enabled && (
            <MpesaPaymentSection
              mpesaSettings={mpesaSettings}
              mpesaMethod={mpesaMethod}
              setMpesaMethod={setMpesaMethod}
              mpesaPhone={mpesaPhone}
              setMpesaPhone={setMpesaPhone}
              loadingC2BPayments={loadingC2BPayments}
              availableC2BPayments={availableC2BPayments}
              fetchAvailableC2BPayments={fetchAvailableC2BPayments}
              selectedC2BPayment={selectedC2BPayment}
              setSelectedC2BPayment={setSelectedC2BPayment}
              c2bMpesaCode={c2bMpesaCode}
              setC2bMpesaCode={setC2bMpesaCode}
              c2bVerifyResult={c2bVerifyResult}
              setC2bVerifyResult={setC2bVerifyResult}
              c2bVerifying={c2bVerifying}
              handleVerifyC2BPayment={handleVerifyC2BPayment}
              formatCurrency={formatCurrency}
            />
          )}
          
          {paymentMethod === "mpesa" && !mpesaSettings?.enabled && (
            <ManualMpesaForm mpesaPhone={mpesaPhone} setMpesaPhone={setMpesaPhone} cardDetails={cardDetails} setCardDetails={setCardDetails} />
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-11" data-testid="checkout-cancel-btn">
            Cancel
          </Button>
          <Button 
            data-testid="pay-button"
            type="button"
            onClick={() => {
              if (activeInvoice) {
                handleCloseInvoice();
              } else {
                handleExpressCheckout();
              }
            }}
            disabled={processing || tenderedNum <= 0 ||
              (paymentMethod === "pesapal_mpesa" && !mpesaPhone?.trim()) ||
              (paymentMethod === "dpo_mpesa" && !mpesaPhone?.trim()) ||
              (paymentMethod === "mpesa" && mpesaMethod === "c2b_verify" && c2bVerifyResult?.found && !c2bVerifyResult?.can_use) || 
              (paymentMethod === "mpesa" && mpesaMethod === "c2b_verify" && !selectedC2BPayment && !c2bMpesaCode.trim())}
            className={cn("w-full sm:w-auto h-11",
              isPartialPayment && "bg-amber-600 hover:bg-amber-700",
              paymentMethod === "mpesa" && mpesaMethod === "c2b_verify" && "bg-green-600 hover:bg-green-700"
            )}
          >
            {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> :
              paymentMethod === "mpesa" && mpesaMethod === "c2b_verify" ? <CheckCircle className="mr-2 h-4 w-4" /> : <CreditCard className="mr-2 h-4 w-4" />}
            {processing ? "Processing..." :
              isPartialPayment 
                ? `Pay ${formatCurrency(tenderedNum)} (Partial)`
                : paymentMethod === "dpo_card"
                  ? `Push ${formatCurrency(finalTotal)} to Terminal`
                  : paymentMethod === "pesapal_pos"
                  ? `Push ${formatCurrency(finalTotal)} to Terminal`
                  : paymentMethod === "pesapal_mpesa"
                  ? `Send M-Pesa Prompt · ${formatCurrency(finalTotal)}`
                  : paymentMethod === "dpo_mpesa"
                  ? `Send M-Pesa Prompt · ${formatCurrency(finalTotal)}`
                  : paymentMethod === "mpesa" && mpesaMethod === "c2b_verify" 
                  ? (selectedC2BPayment 
                      ? `Settle ${formatCurrency(selectedC2BPayment.amount)}` 
                      : `Settle ${formatCurrency(tenderedNum)}`)
                  : `Pay ${formatCurrency(tenderedNum)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Sub-components (Card, NFC, M-Pesa, Credit sections unchanged)

function CardPaymentForm({ cardDetails, setCardDetails }) {
  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CreditCard className="h-4 w-4" />Card Details
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Card Number</Label>
          <Input placeholder="4111 1111 1111 1111" value={cardDetails.card_number}
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
              const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
              setCardDetails({...cardDetails, card_number: formatted.slice(0, 19)});
            }} maxLength={19} data-testid="card-number-input" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Month</Label>
            <Input placeholder="MM" value={cardDetails.expiry_month}
              onChange={(e) => setCardDetails({...cardDetails, expiry_month: e.target.value.replace(/\D/g, '').slice(0, 2)})}
              maxLength={2} data-testid="card-expiry-month-input" />
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Input placeholder="YYYY" value={cardDetails.expiry_year}
              onChange={(e) => setCardDetails({...cardDetails, expiry_year: e.target.value.replace(/\D/g, '').slice(0, 4)})}
              maxLength={4} data-testid="card-expiry-year-input" />
          </div>
          <div>
            <Label className="text-xs">CVV</Label>
            <Input type="password" placeholder="***" value={cardDetails.cvv}
              onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value.replace(/\D/g, '').slice(0, 4)})}
              maxLength={4} data-testid="card-cvv-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cardholder Name</Label>
          <Input placeholder="John Doe" value={cardDetails.cardholder_name}
            onChange={(e) => setCardDetails({...cardDetails, cardholder_name: e.target.value})}
            data-testid="card-holder-name-input" />
        </div>
      </div>
      <div className="flex gap-1 text-xs text-muted-foreground">
        <Badge variant="outline" className="text-[10px]">Visa</Badge>
        <Badge variant="outline" className="text-[10px]">Mastercard</Badge>
        <Badge variant="outline" className="text-[10px]">Amex</Badge>
      </div>
    </div>
  );
}

function NFCPaymentSection({ nfcPaymentStatus, setNfcPaymentStatus, nfcPollingInterval, setNfcPaymentRequestId }) {
  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium"><Smartphone className="h-4 w-4" />NFC Tap-to-Pay</div>
      {nfcPaymentStatus === "idle" && (
        <div className="text-center py-4 space-y-3">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto">
            <Smartphone className="h-8 w-8 text-blue-600" />
          </div>
          <div><p className="text-sm font-medium">Contactless Card Payment</p>
            <p className="text-xs text-muted-foreground">Payment will be processed on an NFC-enabled Android device</p></div>
          {typeof window !== 'undefined' && window.HostPointNFCBridge?.isAvailable?.() ? (
            <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded text-xs text-green-700 dark:text-green-300">NFC device detected</div>
          ) : (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded text-xs text-amber-700 dark:text-amber-300">Use an Android device with HostPoint POS app</div>
          )}
        </div>
      )}
      {nfcPaymentStatus === "pending" && (<div className="text-center py-4"><Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" /><p className="text-sm mt-2">Creating payment request...</p></div>)}
      {nfcPaymentStatus === "waiting" && (
        <div className="text-center py-4 space-y-3">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto animate-pulse"><CreditCard className="h-8 w-8 text-blue-600" /></div>
          <p className="text-sm font-medium">Waiting for card tap...</p>
          <Button variant="outline" size="sm" onClick={() => { if (nfcPollingInterval) clearInterval(nfcPollingInterval); setNfcPaymentStatus("idle"); setNfcPaymentRequestId(null); }}>Cancel</Button>
        </div>
      )}
      {nfcPaymentStatus === "processing" && (<div className="text-center py-4"><Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" /><p className="text-sm mt-2">Processing...</p></div>)}
      {nfcPaymentStatus === "success" && (<div className="text-center py-4"><div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto"><Check className="h-8 w-8 text-green-600" /></div><p className="text-sm font-medium text-green-600 mt-2">Payment Successful!</p></div>)}
      {nfcPaymentStatus === "failed" && (<div className="text-center py-4 space-y-3"><div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto"><X className="h-8 w-8 text-red-600" /></div><p className="text-sm font-medium text-red-600">Payment Failed</p><Button variant="outline" size="sm" onClick={() => setNfcPaymentStatus("idle")}>Try Again</Button></div>)}
    </div>
  );
}

function MpesaPaymentSection({
  mpesaSettings, mpesaMethod, setMpesaMethod, mpesaPhone, setMpesaPhone,
  loadingC2BPayments, availableC2BPayments, fetchAvailableC2BPayments,
  selectedC2BPayment, setSelectedC2BPayment, c2bMpesaCode, setC2bMpesaCode,
  c2bVerifyResult, setC2bVerifyResult, c2bVerifying, handleVerifyC2BPayment, formatCurrency
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-muted-foreground">Payment Method:</span>
        <div className={`grid gap-2 ${mpesaSettings?.c2b_enabled ? 'grid-cols-3' : 'grid-cols-1'}`}>
          <Button variant={mpesaMethod === "stk_push" ? "default" : "outline"} size="sm" className="text-xs h-9"
            onClick={() => { setMpesaMethod("stk_push"); setC2bVerifyResult(null); }} data-testid="mpesa-stk-push-btn">STK Push</Button>
          {mpesaSettings?.c2b_enabled && (
            <>
              <Button variant={mpesaMethod === "c2b" ? "default" : "outline"} size="sm" className="text-xs h-9"
                onClick={() => { setMpesaMethod("c2b"); setC2bVerifyResult(null); }} data-testid="mpesa-c2b-btn">Paybill Info</Button>
              <Button variant={mpesaMethod === "c2b_verify" ? "default" : "outline"} size="sm"
                className="text-xs h-9 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => { setMpesaMethod("c2b_verify"); }} data-testid="mpesa-c2b-verify-btn">Verify C2B</Button>
            </>
          )}
        </div>
      </div>
      {mpesaMethod === "stk_push" && (
        <div className="space-y-2">
          <Input placeholder="M-Pesa Phone (e.g., 0712345678)" value={mpesaPhone}
            onChange={(e) => setMpesaPhone(e.target.value)} type="tel" className="h-11" data-testid="mpesa-phone-input" />
          <p className="text-xs text-muted-foreground">Customer will receive STK push prompt on their phone</p>
        </div>
      )}
      {mpesaMethod === "c2b" && (
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 text-sm">
          <p className="font-medium text-green-800 dark:text-green-300 mb-1">Customer pays via Paybill</p>
          <p className="text-green-700 dark:text-green-400 text-xs">Click &quot;Pay&quot; to see payment instructions</p>
        </div>
      )}
      {mpesaMethod === "c2b_verify" && (
        <C2BVerifySection
          loadingC2BPayments={loadingC2BPayments} availableC2BPayments={availableC2BPayments}
          fetchAvailableC2BPayments={fetchAvailableC2BPayments} selectedC2BPayment={selectedC2BPayment}
          setSelectedC2BPayment={setSelectedC2BPayment} c2bMpesaCode={c2bMpesaCode}
          setC2bMpesaCode={setC2bMpesaCode} c2bVerifyResult={c2bVerifyResult}
          setC2bVerifyResult={setC2bVerifyResult} c2bVerifying={c2bVerifying}
          handleVerifyC2BPayment={handleVerifyC2BPayment} formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
}

function C2BVerifySection({ loadingC2BPayments, availableC2BPayments, fetchAvailableC2BPayments,
  selectedC2BPayment, setSelectedC2BPayment, c2bMpesaCode, setC2bMpesaCode,
  c2bVerifyResult, setC2bVerifyResult, c2bVerifying, handleVerifyC2BPayment, formatCurrency }) {
  return (
    <div className="space-y-3">
      {loadingC2BPayments ? (
        <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin mr-2" /><span className="text-sm text-muted-foreground">Loading...</span></div>
      ) : availableC2BPayments.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-green-700 dark:text-green-400">Available ({availableC2BPayments.length})</p>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => fetchAvailableC2BPayments()}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {availableC2BPayments.map((payment) => (
              <div key={payment.trans_id} onClick={() => { setSelectedC2BPayment(payment); setC2bMpesaCode(payment.trans_id); }}
                className={cn("p-3 rounded-lg border cursor-pointer transition-all",
                  selectedC2BPayment?.trans_id === payment.trans_id ? "bg-green-100 dark:bg-green-900/40 border-green-500" : "bg-muted/50 hover:bg-muted border-transparent")}>
                <div className="flex justify-between items-start">
                  <div><p className="font-mono text-sm font-medium">{payment.trans_id}</p>
                    <p className="text-xs text-muted-foreground">{payment.phone && <span>{payment.phone} &bull; </span>}{payment.sender_name?.trim() && <span>{payment.sender_name.trim()} &bull; </span>}{payment.account_ref && <span>Acc: {payment.account_ref}</span>}</p></div>
                  <div className="text-right"><p className="font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                    {selectedC2BPayment?.trans_id === payment.trans_id && <CheckCircle className="h-4 w-4 text-green-600 ml-auto mt-1" />}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-700 dark:text-yellow-400">No pending payments found</p>
          <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">Enter the M-Pesa code manually below</p>
        </div>
      )}
      <div className="pt-2 border-t">
        <p className="text-xs text-muted-foreground mb-2">{availableC2BPayments.length > 0 ? "Or search/enter code:" : "Enter last 3+ digits:"}</p>
        <div className="flex gap-2">
          <Input placeholder="Last 3+ digits or full code" value={c2bMpesaCode}
            onChange={(e) => { const v = e.target.value.toUpperCase(); setC2bMpesaCode(v); setC2bVerifyResult(null);
              if (selectedC2BPayment && v !== selectedC2BPayment.trans_id) setSelectedC2BPayment(null);
              if (v.length >= 3) fetchAvailableC2BPayments(v); else if (v.length === 0) fetchAvailableC2BPayments();
            }} className="h-11 font-mono uppercase" data-testid="c2b-code-input" />
          <Button variant="outline" onClick={handleVerifyC2BPayment} disabled={c2bVerifying || !c2bMpesaCode.trim()} className="h-11 px-4" data-testid="c2b-verify-btn">
            {c2bVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {c2bVerifyResult && !selectedC2BPayment && (
        <div className={`p-3 rounded-lg border text-sm ${c2bVerifyResult.found && !c2bVerifyResult.can_use ? "bg-red-50 dark:bg-red-900/20 border-red-200" : c2bVerifyResult.found && c2bVerifyResult.can_use ? "bg-green-50 dark:bg-green-900/20 border-green-200" : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200"}`}>
          {c2bVerifyResult.found && !c2bVerifyResult.can_use ? (
            <div className="text-red-700 dark:text-red-300"><p className="font-medium">Already Used</p><p className="text-xs">Used for invoice {c2bVerifyResult.invoice_number}</p></div>
          ) : c2bVerifyResult.found && c2bVerifyResult.can_use ? (
            <div className="text-green-700 dark:text-green-300"><p className="font-medium">Payment Found</p><p className="text-xs">Amount: {formatCurrency(c2bVerifyResult.amount)}</p></div>
          ) : (
            <div className="text-yellow-700 dark:text-yellow-300"><p className="font-medium">Not in System</p><p className="text-xs">Will be recorded on settlement</p></div>
          )}
        </div>
      )}
    </div>
  );
}

function ManualMpesaForm({ mpesaPhone, setMpesaPhone, cardDetails, setCardDetails }) {
  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium"><Phone className="h-4 w-4" />M-Pesa (Manual)</div>
      <div className="space-y-3">
        <div><Label className="text-xs">M-Pesa Reference *</Label>
          <Input placeholder="e.g., SJ13KH7GE2" value={cardDetails.manual_ref || ""}
            onChange={(e) => setCardDetails({...cardDetails, manual_ref: e.target.value.toUpperCase()})}
            className="h-11 font-mono uppercase" data-testid="mpesa-manual-ref-input" /></div>
        <div><Label className="text-xs">Phone (optional)</Label>
          <Input placeholder="e.g., 0712345678" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)}
            type="tel" className="h-10" data-testid="mpesa-manual-phone-input" /></div>
      </div>
    </div>
  );
}

function ManualCardForm({ cardDetails, setCardDetails }) {
  return (
    <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium"><CreditCard className="h-4 w-4" />Card (Manual)</div>
      <div className="space-y-3">
        <div><Label className="text-xs">Last 4 Digits *</Label>
          <Input placeholder="e.g., 4532" value={cardDetails.card_last4 || ""}
            onChange={(e) => setCardDetails({...cardDetails, card_last4: e.target.value.replace(/\D/g, '').slice(0, 4)})}
            maxLength={4} className="h-11 font-mono" data-testid="card-manual-last4-input" /></div>
        <div><Label className="text-xs">Transaction Reference *</Label>
          <Input placeholder="e.g., AUTH-123456" value={cardDetails.manual_ref || ""}
            onChange={(e) => setCardDetails({...cardDetails, manual_ref: e.target.value.toUpperCase()})}
            className="h-11 font-mono uppercase" data-testid="card-manual-ref-input" /></div>
      </div>
      <div className="flex gap-1 text-xs text-muted-foreground">
        <Badge variant="outline" className="text-[10px]">Visa</Badge>
        <Badge variant="outline" className="text-[10px]">Mastercard</Badge>
      </div>
    </div>
  );
}

function CreditPaymentSection({ selectedCustomer, setSelectedCustomer, creditCheckResult, checkCreditAvailability, finalTotal, outstanding, formatCurrency, creditApprovalPending, canCreditSale, customerSearch, setCustomerSearch, fetchCustomers, customersLoading, customersList }) {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (setCustomerSearch) setCustomerSearch(val);
    if (val.length >= 2 && fetchCustomers) fetchCustomers(val);
  };

  const handleSelectCustomer = (cust) => {
    if (setSelectedCustomer) setSelectedCustomer(cust);
    setSearchTerm("");
    if (cust?.is_debtor && checkCreditAvailability) {
      checkCreditAvailability(outstanding || finalTotal);
    }
  };

  // No customer selected yet — show search
  if (!selectedCustomer?.is_debtor) {
    return (
      <div className="space-y-3" data-testid="credit-customer-search">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserCheck className="h-4 w-4 text-primary" />
          <span>Select Credit Customer</span>
        </div>
        <p className="text-xs text-muted-foreground">Search for a debtor customer to post this invoice to their credit account</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customer name, phone, or company..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 h-10"
            data-testid="credit-customer-search-input"
          />
        </div>
        {customersLoading && (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Searching...</div>
        )}
        {!customersLoading && customersList?.length > 0 && searchTerm.length >= 2 && (
          <ScrollArea className="max-h-48">
            <div className="space-y-1">
              {customersList.filter(c => c.is_debtor).map(cust => {
                const available = Math.max(0, (cust.credit_limit || 0) - (cust.current_balance || 0));
                const canAfford = available >= (outstanding || finalTotal);
                return (
                  <div
                    key={cust.id}
                    onClick={() => canAfford && handleSelectCustomer(cust)}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer transition-all",
                      canAfford ? "hover:bg-primary/5 hover:border-primary/30" : "opacity-60 cursor-not-allowed bg-muted/30"
                    )}
                    data-testid={`credit-customer-${cust.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{cust.name}</p>
                          <p className="text-xs text-muted-foreground">{cust.phone || cust.company_name || ""}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn("text-sm font-mono font-semibold", canAfford ? "text-green-600" : "text-red-500")}>
                          {formatCurrency(available)}
                        </p>
                        <p className="text-[10px] text-muted-foreground">available</p>
                      </div>
                    </div>
                    {!canAfford && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />Insufficient credit limit
                      </p>
                    )}
                  </div>
                );
              })}
              {customersList.filter(c => c.is_debtor).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No debtor customers found</p>
              )}
            </div>
          </ScrollArea>
        )}
        {searchTerm.length >= 2 && !customersLoading && (!customersList || customersList.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-4">No customers found</p>
        )}
      </div>
    );
  }

  // Customer is selected — show credit details
  return (
    <div className="space-y-3">
      <div className="p-4 bg-muted/50 rounded-lg space-y-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-primary" />
            <span className="font-medium">Credit: {selectedCustomer.name}</span>
          </div>
          {canCreditSale && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" 
              onClick={() => { if (setSelectedCustomer) setSelectedCustomer(null); }}
              data-testid="change-credit-customer-btn"
            >
              <X className="h-3 w-3 mr-1" />Change
            </Button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div><p className="text-muted-foreground">Limit</p><p className="font-mono font-semibold">{formatCurrency(selectedCustomer.credit_limit || 0)}</p></div>
          <div><p className="text-muted-foreground">Balance</p><p className="font-mono font-semibold text-red-600">{formatCurrency(selectedCustomer.current_balance || 0)}</p></div>
          <div><p className="text-muted-foreground">Available</p><p className="font-mono font-semibold text-green-600">{formatCurrency(Math.max(0, (selectedCustomer.credit_limit || 0) - (selectedCustomer.current_balance || 0)))}</p></div>
        </div>
      </div>
      {creditCheckResult && (
        <div className={cn("p-3 rounded-lg border", creditCheckResult.allowed ? "bg-green-50 dark:bg-green-900/20 border-green-200" : "bg-red-50 dark:bg-red-900/20 border-red-200")}>
          {creditCheckResult.allowed ? (
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300"><CheckCircle className="h-5 w-5" /><div><p className="font-medium">Credit Available</p><p className="text-xs">{formatCurrency(outstanding || finalTotal)} can be posted</p></div></div>
          ) : (
            <div className="flex items-start gap-2 text-red-700 dark:text-red-300"><AlertTriangle className="h-5 w-5 mt-0.5" /><div><p className="font-medium">Insufficient Credit</p><p className="text-xs">{creditCheckResult.reason}</p></div></div>
          )}
        </div>
      )}
      {creditApprovalPending && (
        <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200"><div className="flex items-center gap-2 text-blue-700"><Loader2 className="h-5 w-5 animate-spin" /><p className="font-medium">Approval Pending...</p></div></div>
      )}
      <p className="text-xs text-muted-foreground">Posted to customer account.{selectedCustomer.credit_terms_days > 0 && ` Due in ${selectedCustomer.credit_terms_days} days.`}</p>
    </div>
  );
}
